/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import DOMPurify from 'dompurify';
import React from 'react';
import {IndexedObject} from './interfaces';

declare global {
    // noinspection JSUnusedGlobalSymbols
    interface Window {
        log: Function;
        settings: IndexedObject<any>;
        allowCookies: boolean;
        gtag?: Function;
        gapi?: any;
    }
}

export const getSetting = (name: string): any => {
    const settings: IndexedObject<any> = window.settings;
    if (settings.hasOwnProperty(name))
        return settings[name];
    console.warn(`Global settings does not contain a value for '${name}'`);
};

export const getMode = (): 'prod' | 'test' | 'dev' | 'accept' => {
    return window.settings.mode;
};

export const log = (message: any, obj?: any, force: boolean = false) => {
    if (getMode() !== 'prod' || force) {
        if (obj !== undefined && obj !== null) console.log(message, obj);
        else console.log(message);
    }
};

export const warn = (message: string | number | boolean | object, obj?: any, force: boolean = false) => {
    if (getMode() !== 'prod' || force) {
        if (obj !== undefined && obj !== null) console.warn(message, obj);
        else console.warn(message);
    }
};

export const error = (message: string | number | boolean, obj?: any, force: boolean = false) => {
    if (getMode() !== 'prod' || force) {
        if (obj !== undefined && obj !== null) console.error(message, obj);
        else console.error(message);
    }
};

export const debug = (message: string | number | boolean | object, obj?: any, force: boolean = false) => {
    if (getMode() !== 'prod' || force) {
        if (obj !== undefined && obj !== null) console.debug(message, obj);
        else console.debug(message);
    }
};

const getRootFontSize = (): number => {
    const root = document.documentElement,
        fontString = window.getComputedStyle(root, null).getPropertyValue('font-size');
    return parseInt(fontString.slice(0, fontString.length - 2), 10);
};

export const roundTo = (number: number, digits: number = 0): string => {
    const isNegative = number < 0, multiplicator = Math.pow(10, digits);
    if (isNegative) number = number * -1;
    number = parseFloat((number * multiplicator).toFixed(11));
    let numberString = (Math.round(number) / multiplicator).toFixed(digits);
    if (isNegative)
        numberString = (parseFloat(numberString) * -1).toFixed(digits);
    return numberString;
};

export const roundToNumber = (number: number, decimals: number = 0): number => {
    return Number(roundTo(number, decimals).replace(',', '.'));
};

export const parseMoney = (money: number): string => {
    let moneyString: string;
    if (money === 0 || money % 1 === 0) moneyString = money + ',-';
    else moneyString = roundTo(money, 2).replace('.', ',');
    if (/^0+[1-9]/.test(moneyString)) moneyString = moneyString.replace(/^0+/, '');
    return moneyString;
};

export const capitalize = (text: string): string => text.length ?
    text.substr(0, 1).toUpperCase() + text.substr(1) : text;

export const getSearchParams = (search?: string, removeQuery: boolean = false): URLSearchParams => {
    search = search === undefined || search === null ? location.search : search;
    const params = new URLSearchParams(search);
    if (removeQuery) window.history.pushState({}, document.title, window.location.pathname);
    return params;
};

export const downloadFile = (blob: Blob, fileName: string) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.type = 'download';
    link.download = fileName;
    link.click();
};

export const downloadRequiredFile = (path: string | { default: string }, fileName?: string) => {
    if (typeof path !== 'string') path = path.default;
    const name = getFileName(path, fileName);
    const link = document.createElement('a');
    link.href = path;
    link.type = 'download';
    link.download = name;
    link.click();
};

const getFileName = (path: string, fileName?: string): string => {
    if (fileName?.length) return fileName;
    let pathName = path.substr(path.lastIndexOf('/') + 1, path.length);
    const cacheReg = /-[a-zA-Z\d]+-cache/;
    if (pathName.match(cacheReg))
        pathName = pathName.replace(cacheReg, '');
    return pathName;
};

export const preventDefault = (evt: Event | React.SyntheticEvent) => {
    evt.preventDefault();
};

export const shuffleArray = (arr: any[]): any[] => {
    let counter: number = arr.length;
    if (counter === 0) return arr;
    while (counter > 0) {
        const randomIndex = Math.floor(Math.random() * counter--), temp = arr[counter];
        arr[counter] = arr[randomIndex];
        arr[randomIndex] = temp;
    }
    return arr;
};

export function nullify<T extends string | Array<any> | Set<any> | number | bigint>(value?: T | null): T | null {
    if (typeof value === 'number' || typeof value === 'bigint') {
        if (value === 0) return null;
        return value;
    }
    if (!value) return null;
    else if (Array.isArray(value) || typeof value === 'string')
        return value.length ? value : null;
    else if (value instanceof Set)
        return value.size ? value : null;
    return null;
};

export const undefine = (value?: string | null): string | undefined => {
    return value?.length ? value : undefined;
};

export const sanitizeHtml = (html: string) => {
    return DOMPurify().sanitize(html);
};

export const copyToClipboard = (text: string): Promise<void> => {
    return copyClipboard(text);
};

export const copyClipboard = (text: string): Promise<void> => {
    return window.navigator.clipboard.writeText(text);
};

export function webpSupported(): boolean {
    try {
        return document.createElement('canvas').toDataURL('image/webp')
            .indexOf('data:image/webp') === 0;
    } catch {
        return false;
    }
}

export function isAbsoluteLink(link: string): boolean {
    return /^(https?|www).*/.test(link);
}